import { ConfirmCancelButtons } from "@components/Buttons/ConfirmCancelButtons";
import WarningModal from "@components/Modals/WarningModal";
import LoadingIcon from "@common/LoadingIcon";

import { AdminUserInfoCard } from "@components/Forms/CreateTenantForm/components/AdminUserInfoCard";
import { ContractStatusCard } from "@components/Forms/common/components/tenant/ContractStatusCard";
import { SecuritySettingsCard } from "@components/Forms/common/components/tenant/SecuritySettingsCard";
import { TenantBasicInfoCard } from "@components/Forms/common/components/tenant/TenantBasicInfoCard";
import { ShoppersCloudSettingsCard } from "@components/Forms/common/components/tenant/ShoppersCloudSettingsCard";

import messages from "@constants/Messages";
import { useCreateTenantForm } from "@components/Forms/CreateTenantForm/hooks/useCreateTenantForm";
import { PermissionsCard } from "./components/PermissionsCard";
import { showAvailableKeycloakGroups } from "@services/API/Requests/TenantRequests";
import { useEffect, useState } from "react";

const CreateTenantForm: React.FC = () => {
  const form = useCreateTenantForm();
  const [keycloakGroups, setKeycloakGroups] = useState([]);

  const fetchKeycloakGroups = async () => {
    const groups = await showAvailableKeycloakGroups(
      form.tenantData?.tenant_code || undefined,
      form.setLoading,
      form.setError,
    );
    if (groups) {
      setKeycloakGroups(groups);
    }
    form.setLoading(false);
  };
  useEffect(() => {
    fetchKeycloakGroups();
  }, []);

  return (
    <div>
      {form.requestLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-brightness-50 min-h-screen">
          <div className="bg-white bg-opacity-95 p-5 rounded-lg shadow-lg flex items-center justify-center">
            <LoadingIcon bodyText={messages.tenant.create_loading_text} />
          </div>
        </div>
      )}

      <div className="flex flex-row justify-end">
        {form.error && (
          <button
            className="button-default"
            onClick={form.handleOpenWarningModal}
          >
            {messages.generic.show_error}
          </button>
        )}
      </div>

      <form
        onSubmit={form.handleSubmit}
        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
      >
        <ContractStatusCard
          contractData={form.contractData}
          setContractData={form.setContractData}
          updateContractData={form.updateContractData}
          appData={form.appData}
          loading={form.loading}
          readOnly={false}
        />

        <TenantBasicInfoCard
          tenantData={form.tenantData}
          updateTenantData={form.updateTenantData}
          errors={form.errors}
          isAdminLoggedIn={true}
          updateError={form.updateError}
          loading={form.loading}
        />

        {form.useHazumApi && form.hasYobelyLicense && (
          <ShoppersCloudSettingsCard
            tenantData={form.tenantData}
            updateTenantData={form.updateTenantData}
            errors={form.errors}
            isCreate={true}
            updateError={form.updateError}
            loading={form.loading}
          />
        )}

        <PermissionsCard
          keycloakGroups={keycloakGroups?.available_keycloak_groups}
          tenantData={form.tenantData}
          updateTenantData={form.updateTenantData}
          loading={form.loading}
          handleCheckboxChange={form.handleKeycloakGroupsCheckboxChange}
          error_message={form.error_message}
        />

        <SecuritySettingsCard
          tenantData={form.tenantData}
          updateTenantData={form.updateTenantData}
          loading={form.loading}
          setError={form.setError}
        />

        <AdminUserInfoCard
          loading={form.loading}
          adminUserData={form.adminUserData}
          updateAdminData={form.updateAdminData}
          updateError={form.updateError}
          errors={form.errors}
          setPasswordModalOpen={form.setPasswordModalOpen}
        />

        <ConfirmCancelButtons
          confirmText={"作成"}
          form={true}
          onCancel={form.onCancel}
        />

        <WarningModal
          title="パスワード初期化"
          body="パスワードを初期化します。よろしいですか？"
          confirmText="初期化する"
          isOpen={form.passwordModalOpen}
          confirmButtonColor="button-confirm"
          onConfirm={form.handleModalConfirm}
          onRequestClose={() => form.setPasswordModalOpen(false)}
        />

        <WarningModal
          title={messages.generic.error_modal_title}
          body={form.error}
          confirmText={""}
          cancelText="戻る"
          isOpen={form.warningModalOpen}
          onRequestClose={form.handleCloseWarningModal}
        />
      </form>
    </div>
  );
};

export default CreateTenantForm;
